import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { geocodeByPlaceId } from "react-google-places-autocomplete";
import { useSubmit } from "react-router-dom";
import * as Yup from "yup";
import * as Constants from "../../../constants/Constants";
import { isValidObject } from "../../../helpers/utils/Utils";
import { GrubPopupErrorHook, GrubErrorHook } from "../../../hooks/index";
import { userRegistration, sendOTP } from "../services/authServices";

export const useSignUp = (props) => {
    //Hooks
    const submit = useSubmit();
    const { error } = GrubPopupErrorHook();
    const { error: errorPopUp } = GrubErrorHook();

    //States
    const [buttonDisable, setButtonDisable] = useState(false);
    const [address, setAddress] = useState("");
    const [addressObj, setAddressObj] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState({});
    const [keyboardOpen, setKeyboardOpen] = useState(false);
    // Refs
    const isSubmittingRef = useRef(false);
    const addressRef = useRef("");
    const nameRef = useRef(false);

    useEffect(() => {
        setTimeout(() => {
            setButtonDisable(false);
        }, 1000);
    }, [buttonDisable]);

    useEffect(() => {
        if (props.directSubmit) {
            handleSubmit();
        }
    }, [props.directSubmit]);

    useEffect(() => {
        if (props.isOpen) {
            setIsFocused(true);
            setTimeout(() => {
                nameRef.current = true;
            }, 2000);
        }
        const onResize = () => {
            if (window.visualViewport.height < window.innerHeight) {
                setKeyboardOpen(true);
            } else {
                setKeyboardOpen(false);
            }
        };

        window.visualViewport.addEventListener("resize", onResize);
        return () =>
            window.visualViewport.removeEventListener("resize", onResize);
    }, [props.isOpen]);
    const formik = useFormik({
        initialValues: {
            name: "",
            last_name: "",
            email: "",
            phone: props.phoneNumber || "",
            city: "",
            state: "",
            country: "",
            zip: "",
            password: "",
            confirmPassword: "",
            address: "",
            addressObj: "",
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .required(Constants.USER_FIRST_NAME_REQUIRED)
                .max(254, Constants.USER_FIRST_NAME_LENGTH_GREATE_THEN_255),
            last_name: Yup.string()
                .required(Constants.USER_LAST_NAME_REQUIRED)
                .max(254, Constants.USER_LAST_NAME_LENGTH_GREATE_THEN_255),
            email: Yup.string()
                .trim()
                .matches(Constants.EMAIL_REGEX, Constants.USER_EMAIL_NOT_VALID)
                .required(Constants.USER_EMAIL_REQUIRED)
                .max(
                    254,
                    Constants.USER_EMAIL_LESS_THEN_OR_EQUAL_255_CHARACTER
                ),
            phone: Yup.string()
                .required(Constants.USER_PHONE_NUMBER_REQUIRED)
                .matches(/^[0-9\b]+$/, Constants.USER_PHONE_NUMBER__NOT_VALID)
                .min(3, Constants.USER_PHONE_NUMBER_MUST_BE_3_DIGITS)
                .max(10, Constants.USER_PHONE_NUMBER_GREATER_THEN_10),
            password: Yup.string()
                .required(Constants.PASSWORD_FIELD_REQUIRED)
                .min(8, Constants.PASSWORD_MUST_BE_8_CHARACHTER),
            confirmPassword: Yup.string()
                .required(Constants.CONFIRM_PASSWORD_FIELD_REQUIRED)
                .test("length-or-match", function (value) {
                    if (!value || value.length < 8) {
                        return this.createError({
                            message:
                                Constants.CONFIRM_PASSWORD_MUST_BE_8_CHARACHTER,
                            path: "confirmPassword",
                        });
                    }
                    if (value !== this.parent.password) {
                        return this.createError({
                            message:
                                Constants.PASSWORD_CONFIRM_PASSWORD_NOT_SAME,
                            path: "confirmPassword",
                        });
                    }
                    return true;
                }),
        }),
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: (values) => {
            // myContext.handleLoading(true);
            setButtonDisable(true);
            submit(values, {
                method: Constants.POST,
                path: Constants.SIGNUP_PATH,
            });
        },
    });

    // Add useEffect to handle phone number pre-fill
    useEffect(() => {
        if (props.phoneNumber) {
            formik.setFieldValue("phone", props.phoneNumber);
        }
    }, [props.phoneNumber]);

    const handleSubmit = async () => {
        // Add validation check before submission
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            if (!address) {
                setErrorMessage(Constants.USER_ADDRESS_REQUIRED);
            }
            formik.setTouched({
                name: true,
                last_name: true,
                email: true,
                phone: true,
                password: true,
                confirmPassword: true,
                address: true,
            });
            return;
        }
        formik.validateForm().then((errors) => {
            // Touch all fields to show validation messages
            // // Only proceed if there are no validation errors and address is selected
            if (Object.keys(errors).length === 0 && address) {
                setLoading(true);
                setButtonDisable(true);
                isSubmittingRef.current = true;
                
                let data = {
                    first_name: formik.values.name,
                    last_name: formik.values.last_name,
                    email: formik.values.email,
                    phone: formik.values.phone,
                    password: formik.values.password,
                    address: addressObj.address,
                    city: addressObj.city,
                    country: addressObj.country,
                    zipcode: addressObj.postal_code,
                    state: addressObj.state,
                };

                try {
                    userRegistration(data)
                        .then((response) => {
                            if (
                                undefined !== response.data.flag &&
                                null !== response.data.flag &&
                                response.data.flag == true
                            ) {
                                localStorage.removeItem("signup_token");
                                let payload = {
                                    error: false,
                                    message: response.data.message,
                                };
                                setServerError(payload);
                                setLoading(false);
                                setTimeout(() => {
                                    setServerError({});
                                    props.handleBackButton(payload);
                                    formik.resetForm();
                                    setAddress("");
                                    setAddressObj({});
                                    setErrorMessage("");
                                }, 3000);
                            }
                        })
                        .catch((err) => {
                            if(err.response.data.code == Constants.UNAUTHORIZE_CODE){
                                setLoading(true);
                                try {
                                    setButtonDisable(true);
                                    sendOTP({
                                        phone_number: `${formik.values.phone}`,
                                        business_name: localStorage.getItem("business_name"),
                                        // phone_update: props.phoneUpdate,
                                        // user_id: props.phoneUpdate ? user?.id : null
                                    }).then(response => {
                                        if (
                                            undefined !== response.data.flag &&
                                            null !== response.data.flag &&
                                            response.data.flag == true
                                        ) {
                                            if(response?.data?.data?.otp_token){
                                                localStorage.setItem('otp_token', response?.data?.data?.otp_token);
                                            }
                                            // addToast({
                                            //     alertStatus: Constants.TOAST_TYPE_SUCESS,
                                            //     alertTitle: 'Success',
                                            //     alertDescription: response.data.message
                                            // });
                                            // props.handleBackButton(false);
                                            errorPopUp({ actionData: err });
                                            props.handlePhoneVerificationOpen(formik.values.phone,response.data.data.otp_code,response.data.data.purpose,false, false);
                                            // props.onOTPSent(formik.values.phone);
                                            setLoading(false);
                                            setButtonDisable(false);
                                            isSubmittingRef.current = false;
                                        }
                                    })
                                    .catch(err => {
                                        setLoading(false);
                                        let actionData = err;
                                        let popupError = error({ actionData });
                                        setServerError(popupError);
                                        if (popupError.error) {
                                            // Set only the message string, not the whole object
                                            setServerError(popupError);
                                            setTimeout(() => {
                                                setServerError({});
                                            }, 5000);
                                        }
                                        isSubmittingRef.current = false;
                                        setButtonDisable(false);
                                    });
                                } catch (error) {
                                    let actionData = error;
                                    // error({ actionData });
                                    let popupError = error({ actionData });
                                    setServerError(popupError);
                                    if (popupError.error) {
                                        setServerError(popupError);
                                        setTimeout(() => {
                                            setServerError({});
                                        }, 5000);
                                    }
                                    setLoading(false);
                                    setButtonDisable(false);
                                    isSubmittingRef.current = false;
                                }
                            }
                            else{
                            let actionData = err;
                            let popupError = error({ actionData });
                            
                            if (popupError.error) {
                                setServerError(popupError);
                                setTimeout(() => {
                                    setServerError({});
                                }, 5000);
                            }
                            isSubmittingRef.current = false;
                            setLoading(false);
                            }
                        });
                } catch (error) {
                    let actionData = error;
                    let popupError = error({ actionData });
                    if (popupError.error) {
                        setServerError(popupError);
                        setTimeout(() => {
                            setServerError({});
                        }, 5000);
                    }
                    isSubmittingRef.current = false;
                    setLoading(false);
                }
            }
        });
    };

    const getAddressData = (add_component) => {
        const ShouldBeComponent = {
            street_number: ["street_number"],
            postal_code: ["postal_code"],
            street: ["street_address", "route"],
            province: [
                "administrative_area_level_1",
                "administrative_area_level_2",
                "administrative_area_level_3",
                "administrative_area_level_4",
                "administrative_area_level_5",
            ],
            city: [
                "locality",
                "sublocality",
                "sublocality_level_1",
                "sublocality_level_2",
                "sublocality_level_3",
                "sublocality_level_4",
            ],
            country: ["country"],
        };
        let address = {
            street_number: "",
            postal_code: "",
            street: "",
            province: "",
            city: "",
            country: "",
        };
        add_component.forEach((component) => {
            for (let shouldBe in ShouldBeComponent) {
                if (ShouldBeComponent[shouldBe].includes(component.types[0])) {
                    address[shouldBe] =
                        shouldBe === "country"
                            ? component.short_name
                            : component.long_name;
                }
            }
        });
        address.address = address.street_number + " " + address.street;
        delete address.street_number;
        delete address.street;
        if (address.country === "US") {
            address.state = address.province;
            delete address.province;
        }
        return address;
    };

    const handleAddressChange = async (val) => {
        setAddress(val);
        if (!val) {
            setAddressObj({});
            return;
        }
        const geocodeObj =
            val && val.value && (await geocodeByPlaceId(val.value.place_id));
        const addressObject =
            geocodeObj && getAddressData(geocodeObj[0].address_components);
        if (addressObject) {
            if (
                isValidObject(addressObject.address?.trim()) &&
                isValidObject(addressObject.city?.trim()) &&
                isValidObject(addressObject.state?.trim()) &&
                isValidObject(addressObject.country?.trim()) &&
                isValidObject(addressObject.postal_code?.trim())
            ) {
                setAddressObj(addressObject);
                setErrorMessage("");
            } else {
                if (!addressObject.address?.trim()) {
                    setErrorMessage(Constants.USER_ENTER_ADDRESS_NOT_FOUND);
                } else if (!addressObject.city?.trim()) {
                    setErrorMessage(
                        Constants.USER_ENTER_ADDRESS_CITY_NOT_FOUND
                    );
                } else if (!addressObject.state?.trim()) {
                    setErrorMessage(
                        Constants.USER_ENTER_ADDRESS_STATE_NOT_FOUND
                    );
                } else if (!addressObject.country?.trim()) {
                    setErrorMessage(
                        Constants.USER_ENTER_ADDRESS_COUNTRY_NOT_FOUND
                    );
                } else if (!addressObject.postal_code?.trim()) {
                    setErrorMessage(
                        Constants.USER_ENTER_ADDRESS_ZIPCODE_NOT_FOUND
                    );
                }
            }
        } else {
            setErrorMessage("");
        }
    };

    const handleKeyDown = (e, nextRef) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (
                nextRef == "submit" &&
                !(
                    formik.values.name.length <= 0 ||
                    formik.errors.name ||
                    formik.values.last_name.length <= 0 ||
                    formik.errors.last_name ||
                    formik.values.email.length <= 0 ||
                    formik.errors.email ||
                    formik.values.phone.length <= 0 ||
                    formik.errors.phone ||
                    formik.values.password.length <= 0 ||
                    formik.errors.password ||
                    buttonDisable
                )
            ) {
                handleSubmit();
            } else {
                const input = document.getElementById(nextRef);
                if (input) {
                    input.focus();
                }
            }
        }
    };
    return {
        formik,
        address,
        errorMessage,
        isSubmittingRef,
        addressRef,
        loading,
        isFocused,
        nameRef,
        serverError,
        keyboardOpen,
        handleKeyDown,
        handleAddressChange,
        handleSubmit,
        setErrorMessage,
        
    };
};
